import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Mic, Square, Trash2, Play, Pause } from "lucide-react";
import { fmt } from "@/components/common/VoicePlayer";

const MAX_SECONDS = 180;

export default function Recorder({ form, set, onBack }) {
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [playing, setPlaying] = useState(false);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const audioElRef = useRef(null);

  const bars = useRef(Array.from({ length: 40 }, () => 0.3 + Math.random() * 0.7)).current;

  const stopTimer = () => { if (timerRef.current) clearInterval(timerRef.current); };

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        const reader = new FileReader();
        reader.onloadend = () => { set("audio_url", reader.result); set("audio_duration", seconds); };
        reader.readAsDataURL(blob);
        stream.getTracks().forEach((t) => t.stop());
      };
      mr.start();
      mediaRef.current = mr;
      setRecording(true);
      setSeconds(0);
      timerRef.current = setInterval(() => {
        setSeconds((s) => {
          if (s + 1 >= MAX_SECONDS) { stop(); return MAX_SECONDS; }
          return s + 1;
        });
      }, 1000);
    } catch (e) {
      toast.error("Microphone access is needed to record. You can also write your story instead.");
    }
  };

  const stop = () => {
    stopTimer();
    if (mediaRef.current && mediaRef.current.state !== "inactive") mediaRef.current.stop();
    setRecording(false);
  };

  const reset = () => {
    set("audio_url", null); set("audio_duration", null); setSeconds(0); setPlaying(false);
  };

  const togglePlay = () => {
    const a = audioElRef.current; if (!a) return;
    if (playing) { a.pause(); setPlaying(false); } else { a.play(); setPlaying(true); }
  };

  useEffect(() => () => stopTimer(), []);

  return (
    <div className="space-y-5">
      <button onClick={onBack} className="text-sm text-neutral-500 hover:text-ink">← Choose a different way</button>

      <div className="bg-ink text-white p-6">
        <div className="flex items-center gap-[3px] h-16 mb-4">
          {bars.map((h, i) => (
            <span key={i} className="flex-1 rounded-full bg-rust origin-center"
              style={{ height: `${h * 100}%`, animation: recording ? `mtj-eq 0.9s ease-in-out ${i * 0.03}s infinite` : "none", opacity: recording ? 1 : 0.4 }} />
          ))}
        </div>
        <div className="flex items-center justify-between">
          <span className="font-mono text-sm text-neutral-300" data-testid="rec-timer">{fmt(seconds)} / 03:00</span>
          <div className="flex items-center gap-3">
            {!form.audio_url && !recording && (
              <button data-testid="rec-start" onClick={start} className="inline-flex items-center gap-2 bg-rust px-5 py-2.5 text-sm font-medium hover:bg-rust-hover transition-colors">
                <Mic className="w-4 h-4" /> Start recording
              </button>
            )}
            {recording && (
              <button data-testid="rec-stop" onClick={stop} className="inline-flex items-center gap-2 bg-white text-ink px-5 py-2.5 text-sm font-medium">
                <Square className="w-4 h-4" /> Stop
              </button>
            )}
            {form.audio_url && !recording && (
              <>
                <button data-testid="rec-play" onClick={togglePlay} className="inline-flex items-center gap-2 bg-white text-ink px-4 py-2.5 text-sm font-medium">
                  {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />} {playing ? "Pause" : "Replay"}
                </button>
                <button data-testid="rec-delete" onClick={reset} className="inline-flex items-center gap-2 border border-neutral-600 px-4 py-2.5 text-sm">
                  <Trash2 className="w-4 h-4" /> Re-record
                </button>
              </>
            )}
          </div>
        </div>
        {form.audio_url && <audio ref={audioElRef} src={form.audio_url} onEnded={() => setPlaying(false)} className="hidden" />}
      </div>
      <p className="text-xs text-neutral-500">We store and preserve your original recording. Maximum 3 minutes.</p>
    </div>
  );
}
